import { includes, values } from "lodash";
import { useFeature } from "@radarly/core/features/use-cases";

export const Platform = {
  Bilibili: "bilibili",
  Blog: "blog",
  Comment: "comment",
  Dailymotion: "dailymotion",
  Douyin: "douyin",
  Facebook: "facebook",
  Feed: "feed",
  Forum: "forum",
  Gplus: "gplus",
  Instagram: "instagram",
  Linkedin: "linkedin",
  LittleRedBook: "littleredbook",
  Media: "media",
  Pinterest: "pinterest",
  Press: "press",
  Radio: "radio",
  Reddit: "reddit",
  Review: "review",
  Sinaweibo: "sinaweibo",
  Television: "television",
  Tiktok: "tiktok",
  Twitch: "twitch",
  Twitter: "twitter",
  Vkontakte: "vkontakte",
  Website: "website",
  Wechat: "wechat",
  Youku: "youku",
  Youtube: "youtube",
} as const;

export type WebPlatform =
  | typeof Platform.Media
  | typeof Platform.Website
  | typeof Platform.Blog
  | typeof Platform.Forum
  | typeof Platform.Comment
  | typeof Platform.Review;

export type OfflinePlatform =
  | typeof Platform.Radio
  | typeof Platform.Press
  | typeof Platform.Television;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Platform = typeof Platform[keyof typeof Platform];

export function isWebPlatform(platform: Platform): platform is WebPlatform {
  return includes(
    [
      Platform.Website,
      Platform.Blog,
      Platform.Forum,
      Platform.Media,
      Platform.Feed,
      Platform.Comment,
    ],
    platform
  );
}

export function isReviewPlatform(
  platform: Platform
): platform is typeof Platform.Review {
  return includes([Platform.Review], platform);
}

export function isOfflineMediaPlatform(platform: Platform) {
  return includes(
    [Platform.Radio, Platform.Press, Platform.Television],
    platform
  );
}

export function isPlatformWithCertifiedAccounts(platform: Platform) {
  return includes(
    [
      Platform.Twitter,
      Platform.Sinaweibo,
      Platform.Facebook,
      Platform.Vkontakte,
    ],
    platform
  );
}

export function isSocialInfluencerPlatform(platform: Platform) {
  return includes(
    [
      Platform.Twitter,
      Platform.Facebook,
      Platform.Vkontakte,
      Platform.Instagram,
      Platform.Dailymotion,
      Platform.Youtube,
      Platform.Twitch,
      Platform.Pinterest,
      Platform.Reddit,
      Platform.Linkedin,
      Platform.Sinaweibo,
      Platform.Wechat,
      Platform.Youku,
      Platform.LittleRedBook,
      Platform.Bilibili,
      Platform.Douyin,
      Platform.Tiktok,
    ],
    platform
  );
}

// eslint-disable-next-line react-hooks/rules-of-hooks
const TWITTER_TO_X = useFeature("twitter_to_x");

const Colors: Record<Platform, string> = {
  [Platform.Bilibili]: "#26B8F6",
  [Platform.Blog]: "#9DCE0D",
  [Platform.Comment]: "#999966",
  [Platform.Dailymotion]: "#00AAFF",
  [Platform.Douyin]: "#5ab8b5",
  [Platform.Facebook]: "#3B5998",
  [Platform.Feed]: "#E3702D",
  [Platform.Forum]: "#C0C",
  [Platform.Gplus]: "#DD4B39",
  [Platform.Instagram]: "#DFA543",
  [Platform.Linkedin]: "#007BB6",
  [Platform.LittleRedBook]: "#F19DA7",
  [Platform.Media]: "#FF6600",
  [Platform.Pinterest]: "#E60023",
  [Platform.Press]: "#FF0066",
  [Platform.Radio]: "#E26A54",
  [Platform.Reddit]: "#FF5700",
  [Platform.Review]: "#7893A1",
  [Platform.Sinaweibo]: "#D81127",
  [Platform.Television]: "#D1458F",
  [Platform.Tiktok]: "#00F3EB",
  [Platform.Twitch]: "#6441A5",
  [Platform.Twitter]: TWITTER_TO_X ? "#000" : "#55ACEE",
  [Platform.Vkontakte]: "#507299",
  [Platform.Website]: "#0BC082",
  [Platform.Wechat]: "#7BB32E",
  [Platform.Youku]: "#EF3F22",
  [Platform.Youtube]: "#FF0000",
};

export function getPlatformColor(platform: Platform) {
  return Colors[platform];
}

export function getAllPlatforms() {
  return values(Platform);
}
